import { useState } from 'react';
import { Box, Typography, Chip, IconButton, useTheme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getChartColors, formatCurrency } from '../utils/chartConfig';
import MetricExplanationModal from './MetricExplanationModal';

/**
 * PaymentComparison - Side-by-side comparison of payment scenarios
 *
 * Shows both upfront and 3-year payment options with key metrics highlighted.
 * Demonstrates that both scenarios result in the same total 5-year outcome.
 *
 * @param {Object} upfront - Upfront payment metrics
 * @param {Object} threeYear - 3-year payment metrics
 * @param {string} activeMode - Currently selected mode ('upfront' or 'threeYear')
 */
export default function PaymentComparison({ upfront, threeYear, activeMode }) {
  const theme = useTheme();
  const colors = getChartColors(theme.palette.mode);
  const [openMetric, setOpenMetric] = useState(null);

  if (!upfront || !threeYear) return null;

  // Highlight the selected option
  const isUpfrontActive = activeMode === 'upfront';
  const isThreeYearActive = activeMode === 'threeYear';

  // Calculate monthly values for display
  const monthlySavings = threeYear.monthlyCashFlow + threeYear.monthlyPayment;
  const monthlyPayment = threeYear.monthlyPayment;

  return (
    <Box sx={{ my: 4 }}>
      {/* Section Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            letterSpacing: '-0.01em',
            mb: 0.5,
          }}
        >
          Payment Scenario Comparison
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Compare cash flow timing between payment options
        </Typography>
      </Box>

      {/* Comparison Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
        }}
      >
        {/* Upfront Card */}
        <Box
          sx={{
            p: 3,
            borderRadius: 2,
            background: isUpfrontActive
              ? 'linear-gradient(135deg, rgba(160, 206, 78, 0.15) 0%, rgba(160, 206, 78, 0.05) 100%)'
              : (theme) => theme.palette.mode === 'dark' ? 'linear-gradient(135deg, rgba(26, 31, 37, 0.95) 0%, rgba(15, 20, 25, 0.98) 100%)' : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 249, 250, 0.95) 100%)',
            border: (theme) => isUpfrontActive
              ? `2px solid ${colors.primary}`
              : `1px solid ${theme.palette.divider}`,
            position: 'relative',
            transition: 'all 0.3s ease',
          }}
        >
          {isUpfrontActive && (
            <Chip
              label="Selected"
              size="small"
              icon={<CheckCircleIcon />}
              sx={{
                position: 'absolute',
                top: -12,
                right: 16,
                background: colors.primary,
                color: '#0A0A0A',
                fontWeight: 600,
                '& .MuiChip-icon': {
                  color: '#0A0A0A',
                },
              }}
            />
          )}

          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            Upfront Payment
          </Typography>

          {/* Metrics */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <MetricRow
              label="Total Cost"
              value={formatCurrency(upfront.totalCost)}
              highlight={false}
            />
            <MetricRow
              label="Year 1 Outlay"
              value={formatCurrency(upfront.year1Outlay)}
              subtext="Full cost paid upfront"
              negative
            />
            <MetricRow
              label="Year 1 Net"
              value={formatCurrency(upfront.year1NetBenefit)}
              negative={upfront.year1NetBenefit < 0}
            />
            <MetricRow
              label="Monthly Cash Flow"
              value={`${formatCurrency(upfront.monthlyCashFlow)}/mo`}
              subtext="After Year 1"
              positive
            />
            <MetricRow
              label="Payback Period"
              value={`${upfront.paybackMonths.toFixed(1)} mo`}
            />

            {/* Divider */}
            <Box sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}`, pt: 2, mt: 1 }}>
              <MetricRow
                label="5-Year Net Profit"
                value={formatCurrency(upfront.fiveYearNetProfit)}
                highlight
                positive
              />
            </Box>
          </Box>
        </Box>

        {/* 3-Year Card */}
        <Box
          sx={{
            p: 3,
            borderRadius: 2,
            background: isThreeYearActive
              ? 'linear-gradient(135deg, rgba(78, 205, 196, 0.15) 0%, rgba(78, 205, 196, 0.05) 100%)'
              : (theme) => theme.palette.mode === 'dark' ? 'linear-gradient(135deg, rgba(26, 31, 37, 0.95) 0%, rgba(15, 20, 25, 0.98) 100%)' : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 249, 250, 0.95) 100%)',
            border: (theme) => isThreeYearActive
              ? `2px solid ${colors.teal}`
              : `1px solid ${theme.palette.divider}`,
            position: 'relative',
            transition: 'all 0.3s ease',
          }}
        >
          {isThreeYearActive && (
            <Chip
              label="Selected"
              size="small"
              icon={<CheckCircleIcon />}
              sx={{
                position: 'absolute',
                top: -12,
                right: 16,
                background: colors.teal,
                color: '#0A0A0A',
                fontWeight: 600,
                '& .MuiChip-icon': {
                  color: '#0A0A0A',
                },
              }}
            />
          )}

          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              mb: 2,
            }}
          >
            3-Year Payment Program
          </Typography>

          {/* Metrics */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <MetricRow
              label="Total Cost"
              value={formatCurrency(threeYear.totalCost)}
              subtext="Same as upfront (no interest)"
              highlight={false}
            />

            {/* Monthly Cash Flow Breakdown - KEY CFO VIEW */}
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                background: 'rgba(78, 205, 196, 0.08)',
                border: '1px solid rgba(78, 205, 196, 0.15)',
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: (theme) => theme.palette.mode === 'dark' ? colors.teal : 'text.primary', fontWeight: 600, mb: 1, display: 'block' }}
              >
                Monthly Cash Flow Breakdown
              </Typography>
              <MetricRow
                label="Monthly Payment"
                value={`${formatCurrency(monthlyPayment)}/mo`}
                metricKey="monthly_payment"
                onInfoClick={setOpenMetric}
              />
              <Box sx={{ my: 1 }}>
                <MetricRow
                  label="Monthly Savings"
                  value={`${formatCurrency(monthlySavings)}/mo`}
                  positive
                  metricKey="monthly_savings"
                  onInfoClick={setOpenMetric}
                />
              </Box>
              <Box sx={{ borderTop: '1px dashed rgba(78, 205, 196, 0.3)', pt: 1 }}>
                <MetricRow
                  label="Monthly Net"
                  value={`+${formatCurrency(threeYear.monthlyCashFlow)}/mo`}
                  positive
                  badge={threeYear.isCashFlowPositiveMonthly ? 'Positive from Day 1' : null}
                  metricKey="monthly_cash_flow"
                  onInfoClick={setOpenMetric}
                />
              </Box>
            </Box>

            <MetricRow
              label="Year 1 Outlay"
              value={formatCurrency(threeYear.annualPayment)}
              subtext="1/3 of total cost"
              positive
              metricKey="year1_outlay"
              onInfoClick={setOpenMetric}
            />
            <MetricRow
              label="Year 1 Net"
              value={formatCurrency(threeYear.year1NetBenefit)}
              positive={threeYear.year1NetBenefit > 0}
              badge={threeYear.year1NetBenefit > 0 ? 'Cash Positive' : null}
              metricKey="year1_net_benefit"
              onInfoClick={setOpenMetric}
            />
            <MetricRow
              label="Profit Every Month"
              value={`+${formatCurrency(monthlySavings - monthlyPayment)}/mo`}
              subtext={`Savings are ${(monthlySavings / monthlyPayment).toFixed(1)}× payment`}
              positive
              badge="No Payback Needed"
              metricKey="no_payback_needed"
              onInfoClick={setOpenMetric}
            />

            {/* Divider */}
            <Box sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}`, pt: 2, mt: 1 }}>
              <MetricRow
                label="5-Year Net Profit"
                value={formatCurrency(threeYear.fiveYearNetProfit)}
                highlight
                positive
                subtext="Equivalent to upfront"
                metricKey="five_year_net_profit"
                onInfoClick={setOpenMetric}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* CFO Perspective Table */}
      <Box
        sx={{
          mt: 3,
          p: 2,
          borderRadius: 2,
          background: (theme) => theme.palette.mode === 'dark' ? 'linear-gradient(135deg, rgba(26, 31, 37, 0.95) 0%, rgba(15, 20, 25, 0.98) 100%)' : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 249, 250, 0.95) 100%)',
          border: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            mb: 2,
          }}
        >
          CFO Perspective: Financial Decision Factors
        </Typography>

        {/* Comparison Table */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 0,
            fontSize: '0.75rem',
          }}
        >
          {/* Header Row */}
          <Box sx={{ p: 1, borderBottom: (theme) => `1px solid ${theme.palette.divider}`, color: 'text.secondary' }}>
            Factor
          </Box>
          <Box sx={{ p: 1, borderBottom: (theme) => `1px solid ${theme.palette.divider}`, color: 'text.secondary', textAlign: 'center' }}>
            Upfront
          </Box>
          <Box sx={{ p: 1, borderBottom: (theme) => `1px solid ${theme.palette.divider}`, color: (theme) => theme.palette.mode === 'dark' ? colors.teal : 'text.primary', textAlign: 'center', fontWeight: 600 }}>
            3-Year Deferred
          </Box>

          {/* Day 1 Cash Outlay */}
          <Box sx={{ p: 1, borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`, color: 'text.secondary' }}>
            Day 1 cash outlay
          </Box>
          <Box sx={{ p: 1, borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`, color: (theme) => theme.palette.mode === 'dark' ? colors.orange : 'text.primary', textAlign: 'center' }}>
            {formatCurrency(upfront.totalCost)}
          </Box>
          <Box sx={{ p: 1, borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`, color: (theme) => theme.palette.mode === 'dark' ? colors.teal : 'text.primary', textAlign: 'center', fontWeight: 600 }}>
            $0
          </Box>

          {/* Day 1 Cash Flow */}
          <Box sx={{ p: 1, borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`, color: 'text.secondary' }}>
            Day 1 cash flow
          </Box>
          <Box sx={{ p: 1, borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`, color: (theme) => theme.palette.mode === 'dark' ? colors.orange : 'text.primary', textAlign: 'center' }}>
            Negative
          </Box>
          <Box sx={{ p: 1, borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`, color: (theme) => theme.palette.mode === 'dark' ? colors.teal : 'text.primary', textAlign: 'center', fontWeight: 600 }}>
            POSITIVE
          </Box>

          {/* Budget Category */}
          <Box sx={{ p: 1, borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`, color: 'text.secondary' }}>
            Budget category
          </Box>
          <Box sx={{ p: 1, borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`, color: 'text.primary', textAlign: 'center' }}>
            Often CapEx
          </Box>
          <Box sx={{ p: 1, borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`, color: (theme) => theme.palette.mode === 'dark' ? colors.teal : 'text.primary', textAlign: 'center', fontWeight: 600 }}>
            Often OpEx
          </Box>

          {/* Tax Treatment */}
          <Box sx={{ p: 1, borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`, color: 'text.secondary' }}>
            Tax treatment
          </Box>
          <Box sx={{ p: 1, borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`, color: (theme) => theme.palette.mode === 'dark' ? colors.teal : 'text.primary', textAlign: 'center', fontWeight: 600 }}>
            May qualify for incentives
          </Box>
          <Box sx={{ p: 1, borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`, color: 'text.primary', textAlign: 'center' }}>
            May expense immediately
          </Box>

          {/* Approval Level */}
          <Box sx={{ p: 1, borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`, color: 'text.secondary' }}>
            Approval level
          </Box>
          <Box sx={{ p: 1, borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`, color: 'text.primary', textAlign: 'center' }}>
            Higher (capital)
          </Box>
          <Box sx={{ p: 1, borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`, color: (theme) => theme.palette.mode === 'dark' ? colors.teal : 'text.primary', textAlign: 'center', fontWeight: 600 }}>
            Lower (operating)
          </Box>

          {/* Funding Model */}
          <Box sx={{ p: 1, borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`, color: 'text.secondary' }}>
            Funding model
          </Box>
          <Box sx={{ p: 1, borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`, color: 'text.primary', textAlign: 'center' }}>
            Capital expenditure
          </Box>
          <Box sx={{ p: 1, borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`, color: (theme) => theme.palette.mode === 'dark' ? colors.teal : 'text.primary', textAlign: 'center', fontWeight: 600 }}>
            Cash flow positive
          </Box>

          {/* Payback Model - KEY INSIGHT */}
          <Box sx={{ p: 1, color: 'text.secondary' }}>
            Payback period
          </Box>
          <Box sx={{ p: 1, color: 'text.primary', textAlign: 'center' }}>
            ~{upfront.paybackMonths.toFixed(0)} months
          </Box>
          <Box sx={{ p: 1, color: (theme) => theme.palette.mode === 'dark' ? colors.teal : 'text.primary', textAlign: 'center', fontWeight: 700 }}>
            NONE - always profitable
          </Box>
        </Box>
      </Box>

      {/* Key Message - Only show for 3-year mode (upfront has a payback period) */}
      {isThreeYearActive && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 1,
            background: 'rgba(78, 205, 196, 0.08)',
            border: '1px solid rgba(78, 205, 196, 0.2)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          <CheckCircleIcon sx={{ color: colors.teal, fontSize: '1.25rem', mt: 0.25 }} />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700, mb: 0.5 }}>
                Always profitable. No payback period.
              </Typography>
              <IconButton
                size="small"
                onClick={() => setOpenMetric('no_payback_needed')}
                sx={{ p: 0.25 }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', '&:hover': { color: colors.teal } }} />
              </IconButton>
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Monthly savings are {(monthlySavings / monthlyPayment).toFixed(1)}× the payment amount. You profit from month 1 through year 3 and beyond.
            </Typography>
          </Box>
        </Box>
      )}

      {/* Credit Approval Disclaimer */}
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mt: 2,
          color: 'text.secondary',
          fontStyle: 'italic',
          textAlign: 'center'
        }}
      >
        3-year deferred payment available to qualified buyers, subject to credit approval.
      </Typography>

      {/* Metric Explanation Modal */}
      <MetricExplanationModal
        metric={openMetric}
        open={Boolean(openMetric)}
        onClose={() => setOpenMetric(null)}
      />
    </Box>
  );
}

/**
 * MetricRow - Individual metric display row with optional info icon
 */
function MetricRow({ label, value, subtext, highlight, positive, negative, badge, metricKey, onInfoClick }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: '0.8rem',
            }}
          >
            {label}
          </Typography>
          {metricKey && onInfoClick && (
            <IconButton
              size="small"
              onClick={() => onInfoClick(metricKey)}
              sx={{ p: 0.25 }}
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: 12,
                  color: 'text.disabled',
                  '&:hover': { color: (theme) => theme.palette.mode === 'dark' ? '#4ECDC4' : '#0D7377' },
                }}
              />
            </IconButton>
          )}
        </Box>
        {subtext && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.disabled',
              fontSize: '0.7rem',
              display: 'block',
            }}
          >
            {subtext}
          </Typography>
        )}
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontWeight: highlight ? 700 : 600,
            fontSize: highlight ? '1.1rem' : '0.95rem',
            color: (theme) => theme.palette.mode === 'dark'
              ? (highlight ? '#A0CE4E' : positive ? '#4ECDC4' : negative ? '#FF6B35' : theme.palette.text.primary)
              : 'text.primary',
          }}
        >
          {value}
        </Typography>
        {badge && (
          <Chip
            label={badge}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.65rem',
              fontWeight: 600,
              mt: 0.5,
              background: (theme) => theme.palette.mode === 'dark' ? 'rgba(78, 205, 196, 0.2)' : 'rgba(13, 115, 119, 0.15)',
              color: (theme) => theme.palette.mode === 'dark' ? '#4ECDC4' : 'text.primary',
              border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(78, 205, 196, 0.3)' : 'rgba(13, 115, 119, 0.25)'}`,
            }}
          />
        )}
      </Box>
    </Box>
  );
}
